import {
    chain, filter, findIndex, get, isEmpty, isNumber, reduce, remove, size,
} from 'lodash';

import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useCampaignPage } from './useCampaignHooks';
import SettingsContext from '../../../../contexts/SettingsContext';
import PositionContext from '../../../../contexts/PositionContext';
import LoadingContext from '../../../../contexts/LoadingContext';
import NotificationContext from '../../../../contexts/NotificationContext';

import {
    CAMPAIGN_TYPES,
    MAX_MESSAGES_COUNT_EMAIL,
    MAX_MESSAGES_COUNT_LINKEDIN,
} from './campaignUtils';
import {
    CampaignMessageType,
    CONTACT_CHANNEL_TYPES,
} from '../../../../constants/ContactChannelTypes';
import { royalblue } from '../../../../constants/Colors';

import CampaignHttpService from '../../../../api/CampaignHttpService';
import Logger from '../../../../services/logger';
import {
    HOTJAR_TAGS,
    TRACKING_EVENTS,
} from '../../../../constants/TrackingConstants';
import TrackingContext from '../../../../contexts/TrackingContext';
import { useCampaignTracking } from './useCampaignTracking';
import {
    EMPLOYER_ROLE,
    TALENT_EXPERT_ROLE,
} from '../../../../constants/UserRole';
import { CampaignMessage } from './CampaignMessage';
import { SEC_IN_DAY, SEC_IN_HOUR } from '../../../../services/UtilsService';
import { convertTextTemplateForEditor } from '../../../email/email-editor/template-text-plugin/TemplateTextUtils';
import { UndoEditorChangesContext } from './message-editor/MessageEditorV2';
import EmployerContext from '../../../../contexts/EmployerContext';
import {
    SendgridStatusCodes,
    StatusCodes,
} from '../../../../constants/APIResponseStatusConfig';

export const getCampaignMessageCompoundChannel = (message) => chain(message.channel)
    .concat(message.type)
    .filter(Boolean)
    .join('-')
    .value();

export const sortCampaignMessagesByCompoundChannelCallback = (message) => {
    if (isEmpty(message.type) || !isNumber(message.index)) {
        return 1;
    }

    let weight = message.index;

    if (message.type === CampaignMessageType.applyOnWebsite) {
        weight += 100;
    }

    if (message.type === CampaignMessageType.rejectFollowUp) {
        weight += 200;
    }

    return weight;
};

export const useCampaignMessages = ({
    setInitialMessagesCount,
    setRemovedMessagesCount,
    setCampaignDetails,
    campaignDetails,
    originalCampaignDetails,
    systemMessages,
    setChangedMessages,
}) => {
    const { isJobCampaignPage, isSystemCampaignEditPage } = useCampaignPage();
    const { campaignId } = useParams();
    const { positionId, position } = useContext(PositionContext);
    const {
        startFetchingData,
        stopFetchingData,
        startSavingData,
        stopSavingData,
    } = useContext(LoadingContext);
    const { showSuccessNotification, showErrorNotification } = useContext(NotificationContext);
    const { employerId } = useContext(EmployerContext);
    const {
        trackChanges,
    } = useCampaignTracking({ isJobCampaignPage, campaignDetails, originalCampaignDetails });
    const { trackEvent, trackTag } = useContext(TrackingContext);
    const { t } = useTranslation();
    const {
        systemConfiguration: {
            maxMessagesCountEmail = MAX_MESSAGES_COUNT_EMAIL,
            maxMessagesCountLinkedin = MAX_MESSAGES_COUNT_LINKEDIN,
            maxMessagesCountEmailApplyOnWebSite = 2,
            maxMessagesCountEmailRejectFollowUp = 1,

        },
    } = useContext(SettingsContext);

    const setMessagesCount = useCallback((_campaignDetails) => {
        const allCampaignMessageChannels = [
            CONTACT_CHANNEL_TYPES.EMAIL,
            CONTACT_CHANNEL_TYPES.LINKED_IN,
            getCampaignMessageCompoundChannel({
                channel: CONTACT_CHANNEL_TYPES.EMAIL,
                type: CampaignMessageType.applyOnWebsite,
            }),
            getCampaignMessageCompoundChannel({
                channel: CONTACT_CHANNEL_TYPES.EMAIL,
                type: CampaignMessageType.rejectFollowUp,
            }),
        ];

        const defaultChannelsMessages = reduce(
            allCampaignMessageChannels,
            (acc, value) => ({ ...acc, [value]: [] }),
            {},
        );

        const campaignChannelMessages = chain(_campaignDetails)
            .get('messages', [])
            .filter({ isDeleted: false })
            .groupBy((message) => getCampaignMessageCompoundChannel(message))
            .value();

        const messagesGroupedByChannel = {
            ...defaultChannelsMessages,
            ...campaignChannelMessages,
        };

        const messagesCountByChannel = reduce(
            messagesGroupedByChannel,
            (acc, value, key) => ({ ...acc, [key]: size(value) }),
            {},
        );

        const getMaxMessagesPerChannel = (compoundChannel) => {
            if (compoundChannel === CONTACT_CHANNEL_TYPES.EMAIL) {
                return maxMessagesCountEmail;
            }

            if (compoundChannel === CONTACT_CHANNEL_TYPES.LINKED_IN) {
                return maxMessagesCountLinkedin;
            }

            if (compoundChannel === `${CONTACT_CHANNEL_TYPES.EMAIL}-${CampaignMessageType.applyOnWebsite}`) {
                return maxMessagesCountEmailApplyOnWebSite;
            }

            if (compoundChannel === `${CONTACT_CHANNEL_TYPES.EMAIL}-${CampaignMessageType.rejectFollowUp}`) {
                return maxMessagesCountEmailRejectFollowUp;
            }

            return 0;
        };

        const removedMessagesCountByChannel = reduce(
            messagesGroupedByChannel,
            (acc, value, key) => ({
                ...acc,
                [key]: getMaxMessagesPerChannel(key) - size(value),
            }),
            {},
        );

        setInitialMessagesCount((currentState) => ({
            ...currentState,
            ...messagesCountByChannel,
        }));

        setRemovedMessagesCount((currentState) => ({
            ...currentState,
            ...removedMessagesCountByChannel,
        }));
    }, [maxMessagesCountEmail, maxMessagesCountLinkedin]);

    const onTestMessageSend = useCallback(async (messageData, skipUrl) => {
        try {
            if (messageData.channel === 'linkedin') {
                return;
            }

            startSavingData();
            const response = await CampaignHttpService.sendTestMessage({
                campaignId: originalCampaignDetails._id,
                positionId,
                employerId,
                skipUrl,
                message: {
                    ...messageData,
                    body: `<div style="color: ${royalblue}; margin-bottom: 20px">${t('TEST_MESSAGE_NOTIFICATION')}</div> ${messageData.body}`,
                },
            });

            const responseStatusCode = get(response, 'statusCode');

            const isSuccess = [SendgridStatusCodes.ACCEPTED, StatusCodes.OK].includes(responseStatusCode);

            if (!isSuccess) throw new Error('Error while sending campaign test message');

            showSuccessNotification('TEST_EMAIL_NOTIFICATION');
        } catch (error) {
            Logger.error(error);
            showErrorNotification();
        } finally {
            stopSavingData();
        }
    }, [positionId, originalCampaignDetails, employerId]);

    const onCopyEmails = useCallback(async (targetCampaignId) => {
        try {
            startFetchingData();
            const targetCampaign = await CampaignHttpService.getCampaign(targetCampaignId);
            const campaignMessages = get(targetCampaign, 'messages', []);
            let senderUser = get(targetCampaign, 'senderUser');
            const disabledChannels = get(targetCampaign, 'disabledChannels');

            const role = get(targetCampaign, 'role');

            const shouldComputeSender = (targetCampaign?.type === CAMPAIGN_TYPES.SYSTEM)
                && isEmpty(targetCampaign?.senderUser)
                && Boolean(targetCampaign?.role);

            if (shouldComputeSender) {
                senderUser = await CampaignHttpService.getJobCampaignDefaultSenderByRole({ role, campaignId: campaignDetails._id });
            }

            setCampaignDetails((currentState) => {
                const newCampaignState = {
                    ...currentState,
                    ...(targetCampaign?._id && { parentId: targetCampaign._id }),
                    ...(senderUser && { senderUser }),
                    ...(disabledChannels && { disabledChannels }),
                    messages: campaignMessages,
                };
                const messagesByChannel = chain(newCampaignState.messages)
                    .reject('isDeleted')
                    .groupBy('channel')
                    .value();

                setRemovedMessagesCount({
                    [CONTACT_CHANNEL_TYPES.EMAIL]: MAX_MESSAGES_COUNT_EMAIL - get(messagesByChannel[CONTACT_CHANNEL_TYPES.EMAIL], 'length', 0),
                    [CONTACT_CHANNEL_TYPES.LINKED_IN]: MAX_MESSAGES_COUNT_LINKEDIN - get(messagesByChannel[CONTACT_CHANNEL_TYPES.LINKED_IN], 'length', 0),
                });

                return newCampaignState;
            });

            trackChanges(trackEvent, TRACKING_EVENTS.CAMPAIGN_MESSAGES_EXPORTED_FROM_OTHER_POSITION, {
                campaignName: originalCampaignDetails.name,
                ...(isJobCampaignPage && { campaignCopyEmailsPositionId: targetCampaign?.positionId }),
            });
            trackChanges(trackTag, [HOTJAR_TAGS.CAMPAIGN_MESSAGES_EXPORTED_FROM_OTHER_POSITION], {
                campaignName: originalCampaignDetails.name,
                ...(isJobCampaignPage && { campaignCopyEmailsPositionId: targetCampaign?.positionId }),
            });
        } catch (error) {
            Logger.error(error);
            showErrorNotification();
        } finally {
            stopFetchingData();
        }
    }, [positionId, originalCampaignDetails, campaignDetails, isJobCampaignPage, campaignId]);

    const getChannelMessages = useCallback(({
        campaign: campaignData,
        channel,
        type,
        systemMessages: allSystemMessages,

    }) => {
        const senderUser = get(campaignData, 'senderUser');
        const isPositionEmployerRepExist = get(position, 'employerRepId', null);
        let senderUserRole;

        if (senderUser) {
            senderUserRole = get(senderUser, 'role');
        } else if (isPositionEmployerRepExist) {
            senderUserRole = EMPLOYER_ROLE;
        } else {
            senderUserRole = TALENT_EXPERT_ROLE;
        }

        const isEmployerRepSender = senderUserRole === EMPLOYER_ROLE;
        let messages = [];
        if (isSystemCampaignEditPage) {
            messages = campaignDetails?.role === TALENT_EXPERT_ROLE
                ? allSystemMessages.talenyaMessages
                : allSystemMessages.employerRepMessages;
        } else {
            messages = isEmployerRepSender
                ? allSystemMessages.employerRepMessages
                : allSystemMessages.talenyaMessages;
        }

        const channelMessages = filter(
            messages,
            (m) => m.channel === channel && m.type === type,
        );

        return channelMessages;
    }, [position, campaignDetails]);

    const onMessageAdd = useCallback(({
        isFirstMessageAdd: isFirstMessage,
        channel: compoundChannel,
        newMessageIndex,
    }) => {
        setCampaignDetails((currentState) => {
            const maxIndex = chain(currentState)
                .get('messages', [])
                .map('index')
                .max()
                .value();

            const [channel, type] = chain(compoundChannel).split('-').value();

            const systemChannelMessages = getChannelMessages({
                campaign: currentState,
                channel,
                type,
                systemMessages,
            });

            const getMessageWaitTimeSec = () => {
                if (isEmpty(type)) {
                    return isFirstMessage ? 0 : SEC_IN_DAY * 2;
                }

                return isFirstMessage ? SEC_IN_HOUR : SEC_IN_DAY;
            };

            const systemMessage = get(systemChannelMessages, newMessageIndex);

            const wait = getMessageWaitTimeSec();

            const newMessage = new CampaignMessage({
                channel,
                type,
                index: maxIndex + 1,
                wait,
                name: systemMessage?.name,
                subject: systemMessage?.subject,
                body: systemMessage?.body,
            });

            return {
                ...currentState,
                messages: [
                    ...(currentState.messages || []),
                    newMessage,
                ],
            };
        });

        setRemovedMessagesCount((currentState) => ({
            ...currentState,
            [compoundChannel]: currentState[compoundChannel] - 1,
        }));
    }, [campaignDetails, systemMessages]);

    const onMessageUpdate = useCallback(async (updatedMessage) => {
        try {
            const updatedMessages = chain(campaignDetails)
                .get('messages', [])
                .cloneDeep()
                .value();

            const emailToUpdateIndex = findIndex(updatedMessages, { index: updatedMessage.index, channel: updatedMessage.channel });

            updatedMessages[emailToUpdateIndex] = {
                ...updatedMessages[emailToUpdateIndex],
                ...updatedMessage,
            };

            setCampaignDetails((currentState) => ({
                ...currentState,
                messages: updatedMessages,
            }));

            setChangedMessages((currentState) => {
                remove(currentState, { index: updatedMessage.index, channel: updatedMessage.channel });
                return [
                    ...currentState,
                    { index: updatedMessage.index, channel: updatedMessage.channel },
                ];
            });

            trackChanges(trackEvent, TRACKING_EVENTS.CAMPAIGN_EMAILS_UPDATED, {
                campaignName: originalCampaignDetails.name,
                campaignEmailChannel: updatedMessage.channel,
            });

            trackChanges(trackTag, [HOTJAR_TAGS.CAMPAIGN_EMAILS_UPDATED], {
                campaignName: originalCampaignDetails.name,
                campaignEmailChannel: updatedMessage.channel,
            });
        } catch (error) {
            Logger.error(error);
            showErrorNotification();
        } finally {
            stopSavingData();
        }
    }, [positionId, campaignDetails, originalCampaignDetails, setCampaignDetails, campaignId]);

    const onMessageRemove = useCallback((messageToRemove) => {
        trackChanges(trackEvent, TRACKING_EVENTS.CAMPAIGN_EMAILS_UPDATED, {
            campaignName: originalCampaignDetails.name,
            campaignEmailChannel: messageToRemove.channel,
        });

        trackChanges(trackTag, [HOTJAR_TAGS.CAMPAIGN_EMAILS_UPDATED], {
            campaignName: originalCampaignDetails.name,
            campaignEmailChannel: messageToRemove.channel,
        });

        const messageCompoundChannel = getCampaignMessageCompoundChannel(messageToRemove.message);

        setCampaignDetails((currentState) => {
            const updatedMessages = get(currentState, 'messages', [])
                .map((message) => {
                    if (message.index === messageToRemove.index && messageToRemove.channel === message.channel) {
                        return {
                            ...message,
                            isDeleted: true,
                        };
                    }

                    return message;
                });

            const { channel, type } = messageToRemove.message;

            const isChannelMessagesListEmpty = chain(updatedMessages)
                .filter({ channel, isDeleted: false })
                .filter((m) => m.type === type)
                .isEmpty()
                .value();

            const disabledChannels = chain(currentState)
                .get('disabledChannels', [])
                .concat(isChannelMessagesListEmpty ? messageCompoundChannel : [])
                .uniq()
                .value();

            return {
                ...currentState,
                disabledChannels,
                messages: updatedMessages,
            };
        });

        setRemovedMessagesCount((currentState) => ({
            ...currentState,
            [messageCompoundChannel]: currentState[messageCompoundChannel] + 1,
        }));
    }, [campaignDetails, setCampaignDetails, originalCampaignDetails, setRemovedMessagesCount]);

    const resetMessageData = useCallback(({ index, channel }) => {
        setCampaignDetails((currentState) => {
            const updatedMessages = get(currentState, 'messages', [])
                .map((message) => {
                    if (message.index === index && message.channel === channel) {
                        const originalMessage = chain(originalCampaignDetails)
                            .get('messages', [])
                            .find({ index, channel })
                            .value();
                        const convertedSubject = convertTextTemplateForEditor(originalMessage.subject);
                        const convertedBody = convertTextTemplateForEditor(originalMessage.body);

                        UndoEditorChangesContext.undoMessageChanges({
                            name: originalMessage.name,
                            body: convertedBody,
                            subject: convertedSubject,
                        });

                        return {
                            ...originalMessage,
                        };
                    }

                    return message;
                });

            return {
                ...currentState,
                messages: updatedMessages,
            };
        });
        setChangedMessages((currentState) => {
            remove(currentState, { index, channel });
            return [...currentState];
        });
    }, [campaignDetails, originalCampaignDetails, setCampaignDetails]);

    return {
        setMessagesCount,
        onTestMessageSend,
        onCopyEmails,
        onMessageAdd,
        onMessageUpdate,
        onMessageRemove,
        resetMessageData,
    };
};
